import axios from "axios";
import { Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import EditorTextArea from "../../../components/EditorTextArea";
import FileUploadImage from "../../../components/FileUploadImage";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useLanguage } from "../../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import { SubCategoriesData } from "../../../lib/languages";
import DrawerAddCourse from "../Course/DrawerAddCourse";
import TableCourse from "../Course/TableCourse";
import { useAPI } from "../../../context/APIContext";
import { Spin } from "antd";
import LoaderPage from "../../../components/LoaderPage";

export default function AdminViewSubCategory() {
  const { subCategoriesItems, coursesItems } = useAPI();
  const { loadingSubCategory, subCategories, getAllSubCategories } =
    subCategoriesItems;
  const { loadingCourses, courses, getAllCourses } = coursesItems;

  const { state } = useLocation();
  const category = state?.category;
  const { id } = useParams();
  // Filter subCategories for the current subCategory
  const oneSubCategory = subCategories.filter((subCat) => subCat.id === +id);
  const coursesOfSubCategory =
    courses.filter((course) => course?.sub_category?.id === +id) || [];

  // ------------------------------------

  const { language } = useLanguage();
  const t = SubCategoriesData[language] || SubCategoriesData["en"];

  const [editorRerender, setEditorRerender] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [form, setForm] = useState({
    title_en: undefined,
    title_ar: undefined,
    description_en: undefined,
    description_ar: undefined,
    status: false,
  });

  useEffect(() => {
    setEditorRerender(!editorRerender);
    setImage(oneSubCategory[0]?.image);
    setForm({
      title_en: oneSubCategory[0]?.title?.en,
      title_ar: oneSubCategory[0]?.title?.ar,
      description_en: oneSubCategory[0]?.description?.en,
      description_ar: oneSubCategory[0]?.description?.ar,
      status: oneSubCategory[0]?.status,
    });
  }, [subCategories, id]);

  const handleEditSubCategory = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      if (form.title_ar || form.title_en) {
        formData.append(
          "title",
          JSON.stringify({ ar: form.title_ar, en: form.title_en })
        );
      }
      if (form.description_ar || form.description_en) {
        formData.append(
          "description",
          JSON.stringify({ ar: form.description_ar, en: form.description_en })
        );
      }
      formData.append("image", image ?? "");
      formData.append("status", form.status ? 1 : 0);

      await axios.post(
        `${BaseUrlApi}/categories/${category?.id}/sub-category/${oneSubCategory[0]?.id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast(t.updateSubCategory, {
        description: t.modifySubCategory,
      });

      await getAllSubCategories();
      setEditMode(false);
    } catch (error) {
      setErrors(ErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (loadingSubCategory) {
    return <LoaderPage title={t.loadingSubCategories} />;
  }

  return (
    <div className="space-y-10" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className={"flex items-center justify-between flex-wrap gap-4"}>
        <div className="w-full sm:w-fit">
          <p className="font-bold text-2xl pt-5" id="sub-category">
            {t.updateSubCategory} “
            {oneSubCategory[0]?.title[language] ||
              oneSubCategory[0]?.title.en ||
              oneSubCategory[0]?.title.ar ||
              "---"}
            ”
          </p>
          <p className="font-medium text-sm">{t.modifySubCategory}</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-fit">
          {editMode && (
            <Button
              className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl"
              onClick={handleEditSubCategory}
              disabled={loading || !editMode}
            >
              {loading && (
                <div className="animate-spin">
                  <Loader2 />
                </div>
              )}
              {t.updateSubCategory}
            </Button>
          )}
          <Button
            variant={"outline"}
            className={`w-full sm:w-fit h-10 rounded-xl ${
              editMode
                ? "border-2 border-blue-600 text-blue-600 hover:text-blue-500"
                : "bg-blue-600 text-white hover:bg-blue-600/80 hover:text-white"
            }`}
            onClick={() => setEditMode(!editMode)}
            disabled={loading}
          >
            {editMode ? t.showMode : t.editMode}
          </Button>
        </div>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative">
        <div className="col-span-1 space-y-4 md:sticky top-2 self-start">
          {/* Upload Img */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <p className="font-bold text-xl">{t.thumbnail}</p>
              <FileUploadImage
                onUploadComplete={(url) => setImage(url)}
                image={image}
                name={"sub-categories"}
                disabled={!editMode}
              />
              {errors?.image && (
                <p className="text-red-500 font-semibold text-sm">
                  {errors.image[0]}
                </p>
              )}
              <div className="font-normal text-sm text-muted-foreground text-center">
                {t.onlyImages}
              </div>
            </CardContent>
          </Card>
          {/* Status */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-4 justify-between border-b pb-2">
                <p className="font-bold text-xl">{t.status}</p>
                <div
                  className={`size-4 ${
                    !!form?.status ? "bg-green-500" : "bg-red-500"
                  }  rounded-full`}
                />
              </div>
              <div className="grid gap-2">
                <p className="text-sm">{t.selectStatus}</p>
                <Select
                  disabled={!editMode}
                  value={!!form?.status}
                  onValueChange={(e) => setForm({ ...form, status: e })}
                >
                  <SelectTrigger
                    className="rounded-lg sm:ml-auto"
                    aria-label="Select status"
                  >
                    <SelectValue placeholder={t.selectStatus} />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value={true} className="rounded-lg">
                      {t.active}
                    </SelectItem>
                    <SelectItem value={false} className="rounded-lg">
                      {t.inactive}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="col-span-1 lg:col-span-2">
          {/* General information */}
          <Card className="h-full flex flex-col">
            <CardContent className="p-4 space-y-4 flex-1 flex flex-col">
              <p className="font-bold text-xl border-b pb-2">
                {t.generalInformation}
              </p>
              <div className="space-y-4 flex-1 flex flex-col">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="title_en" className="text-sm">
                      {t.titleEn}
                    </label>
                    <Input
                      readOnly={!editMode}
                      id="title_en"
                      name="title_en"
                      type="text"
                      placeholder="Enter title in English"
                      value={form.title_en}
                      onChange={(e) =>
                        setForm({ ...form, title_en: e.target.value })
                      }
                    />
                    {errors?.title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.title[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="title_ar" className="text-sm">
                      {t.titleAr}
                    </label>
                    <Input
                      readOnly={!editMode}
                      id="title_ar"
                      name="title_ar"
                      type="text"
                      placeholder="Enter title in Arabic"
                      value={form.title_ar}
                      onChange={(e) =>
                        setForm({ ...form, title_ar: e.target.value })
                      }
                    />
                    {errors?.title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.title[0]}
                      </p>
                    )}
                  </div>
                </div>
                {/* Description */}
                <div className="flex flex-col gap-2 flex-1">
                  <label htmlFor="description" className="text-sm">
                    {t.descriptionAr} & {t.descriptionEn}
                  </label>
                  <EditorTextArea
                    EditorRerender={editorRerender}
                    form={form}
                    setForm={setForm}
                    disabled={!editMode}
                    en={"description_en"}
                    ar={"description_ar"}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Courses */}
      <div className="">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold">{t.courses}</h1>
          <DrawerAddCourse
            getAllCourses={getAllCourses}
            item={oneSubCategory[0]}
            type={"sub_category"}
          />
        </div>

        {!loadingCourses ? (
          <TableCourse
            data={coursesOfSubCategory}
            getAllCourses={getAllCourses}
            subCategory={oneSubCategory[0]}
          />
        ) : (
          <div className="h-40 w-full flex items-center justify-center">
            <Spin size="large" />
          </div>
        )}
      </div>
    </div>
  );
}
